<template>
  <div
    class="project-case-study"
    :style="{ '--case-chrome': chromeHeight + 'px' }"
  >
    <div class="case-hero case-hero-text">
      <ProjectPageHeader project-id="early" context="SHSID">
        <h1>How I started building with code</h1>
        <p class="project-subtitle">
          Downloaders, browser extensions, and an economics graph generator
        </p>
      </ProjectPageHeader>
      <div class="case-hero-lede">
        <p>
          During junior high and high school, programming felt like playing
          with building blocks. I could combine a few pieces, change something
          and see what the computer would do. Curiosity led me to try desktop
          apps, browser extensions, websites and automation. Along the way,
          some experiments became tools I used, including a podcast downloader
          and Econographer for drawing economics diagrams in Word.
        </p>
      </div>
    </div>
    <ProjectChapterNav
      ref="chapterNav"
      :chapters="chapters"
      :active-chapter="activeChapter"
      :progress="chapterProgress"
      @navigate="goToChapter"
    />
    <article class="project-narrative case-narrative">
      <section id="case-chapter-0" class="case-section" data-case-chapter>
        <header class="case-section-heading" data-case-reveal>
          <span class="case-eyebrow"><span>01</span>Desktop apps</span>
          <h2 id="how-it-started">My first desktop programs</h2>
        </header>
        <div class="case-pair">
          <div class="case-copy" data-case-reveal>
            <p>
              My interest began at thirteen, when a teacher demonstrated a batch
              script that created folders automatically. Seeing a few lines
              make the computer do something made me curious about what else
              I could get it to do. I started teaching myself Java and Python,
              trying small desktop applications, including a
              contact notebook and a number-guessing game with user accounts and
              a history stored in text files.
            </p>
            <p>
              These screenshots show the contact notebook, one of the first
              applications I completed.
            </p>
          </div>
          <div class="case-stage" data-case-reveal>
            <div class="case-stage-content">
              <div class="scroll-container" data-media-rail="finite" tabindex="0" role="region" aria-label="Contact notebook screenshots">
                <img
                  src="/assets/img/gallery-thumbs/contactnotebook1.jpg.webp"
                  data-full-src="/assets/img/contactnotebook1.jpg"
                  loading="lazy"
                  decoding="async"
                  alt="Contact Notebook Screenshot 1"
                />
                <img
                  src="/assets/img/gallery-thumbs/contactnotebook2.jpg.webp"
                  data-full-src="/assets/img/contactnotebook2.jpg"
                  loading="lazy"
                  decoding="async"
                  alt="Contact Notebook Screenshot 2"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
      <section id="case-chapter-1" class="case-section" data-case-chapter>
        <header class="case-section-heading" data-case-reveal>
          <span class="case-eyebrow"><span>02</span>Podcasts</span>
          <h2
            id="batch-downloading-scientific-american-episodes"
            data-source-anchor="batch-downloading-scientific-american-episodes"
          >
            Taking Scientific American podcasts with me
          </h2>
        </header>
        <div class="case-pair">
          <div class="case-copy" data-case-reveal>
            <p class="chapter-intro">SA Fetcher</p>
            <p>
              In my first year of junior high school, I started using Scientific
              American podcasts to practise English. I listened on an MP3 player
              that could also display images and text, so I built a desktop
              application that downloaded episode audio, transcripts, and images
              in batches. A simple account system recorded which episodes each
              user had downloaded and allowed the application to continue from
              the previous stopping point.
            </p>
          </div>
          <div class="case-stage" data-case-reveal>
            <div class="case-stage-content">
              <div class="scroll-container" data-media-rail="finite" tabindex="0" role="region" aria-label="SA Fetcher screenshots">
                <img
                  src="/assets/img/gallery-thumbs/safetcher1.jpg.webp"
                  data-full-src="/assets/img/safetcher1.jpg"
                  loading="lazy"
                  decoding="async"
                  alt="SA Fetcher Screenshot 1"
                />
                <img
                  src="/assets/img/gallery-thumbs/safetcher2.jpg.webp"
                  data-full-src="/assets/img/safetcher2.jpg"
                  loading="lazy"
                  decoding="async"
                  alt="SA Fetcher Screenshot 2"
                />
                <img
                  src="/assets/img/gallery-thumbs/safetcher3.jpg.webp"
                  data-full-src="/assets/img/safetcher3.jpg"
                  loading="lazy"
                  decoding="async"
                  alt="SA Fetcher Screenshot 3"
                />
                <img
                  src="/assets/img/gallery-thumbs/safetcher4.jpg.webp"
                  data-full-src="/assets/img/safetcher4.jpg"
                  loading="lazy"
                  decoding="async"
                  alt="SA Fetcher Screenshot 4"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
      <section id="case-chapter-2" class="case-section" data-case-chapter>
        <header class="case-section-heading" data-case-reveal>
          <span class="case-eyebrow"><span>03</span>Extensions</span>
          <h2
            id="chrome-extension-experiments"
            data-source-anchor="chrome-extension-experiments"
          >
            Chrome extension experiments
          </h2>
        </header>
        <div class="case-pair">
          <div class="case-copy" data-case-reveal>
            <p>
              In my second year of junior high school, I began exploring Chrome
              extensions. Tools that could change a page’s behaviour, such as
              dimming the screen around a YouTube video, made me want to
              understand how they worked. I learned to develop and publish
              extensions, then made several of my own.
            </p>
            <ul>
              <li>
                <strong>Easy QR Code</strong> generated a QR code for the
                current page or for custom text and links.
              </li>
              <li>
                <strong>Page Muter</strong> muted audio or video in the current
                tab.
              </li>
              <li>
                <strong>Image Fetcher</strong> downloaded a page's images in a
                batch.
              </li>
              <li>
                <strong>SA Fetcher</strong> brought a simplified version of the
                Scientific American downloader into the browser.
              </li>
              <li>
                <strong>WeChat Spamming</strong> automated repeated messages in
                WeChat as a prank tool.
              </li>
            </ul>
            <p>
              These screenshots show the developer dashboard and several of the
              extensions.
            </p>
          </div>
          <div class="case-stage" data-case-reveal>
            <div class="case-stage-content">
              <div class="scroll-container" data-media-rail="finite" tabindex="0" role="region" aria-label="Browser extension screenshots">
                <img
                  src="/assets/img/gallery-thumbs/chromeextension1.png.webp"
                  data-full-src="/assets/img/chromeextension1.png"
                  loading="lazy"
                  decoding="async"
                  alt="Chrome Extension Screenshot 1"
                />
                <img
                  src="/assets/img/gallery-thumbs/chromeextension2.png.webp"
                  data-full-src="/assets/img/chromeextension2.png"
                  loading="lazy"
                  decoding="async"
                  alt="Chrome Extension Screenshot 2"
                />
                <img
                  src="/assets/img/gallery-thumbs/chromeextension3.png.webp"
                  data-full-src="/assets/img/chromeextension3.png"
                  loading="lazy"
                  decoding="async"
                  alt="Chrome Extension Screenshot 3"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
      <section id="case-chapter-3" class="case-section" data-case-chapter>
        <header class="case-section-heading" data-case-reveal>
          <span class="case-eyebrow"><span>04</span>Music</span>
          <h2
            id="music-streaming-hacks"
            data-source-anchor="music-streaming-hacks"
          >
            Music streaming hacks
          </h2>
        </header>
        <div class="case-pair">
          <div class="case-copy" data-case-reveal>
            <p>
              In my first year of high school, I spent several months developing
              a music downloader for Chinese streaming services. Users could
              paste a song-page link into one interface to download music across
              several services.
            </p>
            <p>
              The technical work involved web scraping and reverse engineering
              how the services exposed media URLs. The interface accepted
              song-page links from QQ Music, NetEase Cloud Music, KuGou, and
              KuWo. Users could choose the audio quality and download multiple
              songs from an artist’s catalogue. I also built an account system
              with different levels of access, including restricted lossless downloads.
            </p>
            <p>
              The service ran for about six months and reached 413 registered
              users. It could also retrieve paid tracks without authorisation,
              an implication I had not understood when I began. I closed it once
              I recognised the problem.
            </p>
            <p>
              These screenshots show the Simplified Chinese interface used by
              the original audience.
            </p>
          </div>
          <div class="case-stage" data-case-reveal>
            <div class="case-stage-content">
              <div class="scroll-container" data-media-rail="finite" tabindex="0" role="region" aria-label="Music downloader screenshots">
                <img
                  src="/assets/img/gallery-thumbs/musicdownloader1.jpg.webp"
                  data-full-src="/assets/img/musicdownloader1.jpg"
                  loading="lazy"
                  decoding="async"
                  alt="Music Downloader Screenshot 1"
                />
                <img
                  src="/assets/img/gallery-thumbs/musicdownloader2.jpg.webp"
                  data-full-src="/assets/img/musicdownloader2.jpg"
                  loading="lazy"
                  decoding="async"
                  alt="Music Downloader Screenshot 2"
                />
                <img
                  src="/assets/img/gallery-thumbs/musicdownloader3.jpg.webp"
                  data-full-src="/assets/img/musicdownloader3.jpg"
                  loading="lazy"
                  decoding="async"
                  alt="Music Downloader Screenshot 3"
                />
                <img
                  src="/assets/img/gallery-thumbs/musicdownloader4.jpg.webp"
                  data-full-src="/assets/img/musicdownloader4.jpg"
                  loading="lazy"
                  decoding="async"
                  alt="Music Downloader Screenshot 4"
                />
                <img
                  src="/assets/img/gallery-thumbs/musicdownloader5.jpg.webp"
                  data-full-src="/assets/img/musicdownloader5.jpg"
                  loading="lazy"
                  decoding="async"
                  alt="Music Downloader Screenshot 5"
                />
                <img
                  src="/assets/img/gallery-thumbs/musicdownloader6.jpg.webp"
                  data-full-src="/assets/img/musicdownloader6.jpg"
                  loading="lazy"
                  decoding="async"
                  alt="Music Downloader Screenshot 6"
                />
                <img
                  src="/assets/img/gallery-thumbs/musicdownloader7.jpg.webp"
                  data-full-src="/assets/img/musicdownloader7.jpg"
                  loading="lazy"
                  decoding="async"
                  alt="Music Downloader Screenshot 7"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
      <section id="case-chapter-4" class="case-section" data-case-chapter>
        <header class="case-section-heading" data-case-reveal>
          <span class="case-eyebrow"><span>05</span>Website</span>
          <h2
            id="building-kiwiviews-website"
            data-source-anchor="building-kiwiviews-website"
          >
            Building Kiwiview's website
          </h2>
        </header>
        <div class="case-prose-pair" data-case-reveal>
          <p>
            In my second year of high school, I began building a website for
            Kiwiview International Limited, a New Zealand company co-owned by my
            parents and a local business partner. The company was new, and they
            gave me the opportunity to design and develop its first website.
          </p>
          <p>
            I taught myself HTML, CSS, JavaScript, and SQL as I built the site.
            Deploying it to an IIS server on Tencent Cloud took months of work
            and meant learning how the server, database, domain, and hosting
            environment fitted together.
          </p>
        </div>
      </section>
      <section id="case-chapter-5" class="case-section" data-case-chapter>
        <header class="case-section-heading" data-case-reveal>
          <span class="case-eyebrow"><span>06</span>Automation</span>
          <h2 id="simple-automation" data-source-anchor="simple-automation">
            A script that played arithmetic games
          </h2>
        </header>
        <div class="case-pair">
          <div class="case-copy" data-case-reveal>
            <p>
              In my third year of high school, our school ran online
              mental-arithmetic and 24-point games. I built a script that
              recognised the questions on screen, solved them algorithmically,
              and entered the answers automatically. Each answer brought up
              another question, and the script kept going until the game timer
              ended.
            </p>
            <p>
              The recordings below show how screen recognition, a solver and
              simulated input work together to complete the automation loop.
            </p>
          </div>
          <div class="case-stage" data-case-reveal>
            <div class="case-stage-content">
              <div class="scroll-container" data-media-rail="finite" tabindex="0" role="region" aria-label="Arithmetic game recording">
                <video controls preload="metadata">
                  <source src="/assets/vid/24.mp4" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
                <video controls preload="metadata">
                  <source src="/assets/vid/mentalmath.mp4" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section id="case-chapter-6" class="case-section" data-case-chapter>
        <header class="case-section-heading" data-case-reveal>
          <span class="case-eyebrow"><span>07</span>Diagrams</span>
          <h2
            id="econographer-an-economics-graphs-generator"
            data-source-anchor="econographer-an-economics-graphs-generator"
          >
            Drawing economics diagrams in Word
          </h2>
        </header>
        <div class="case-pair">
          <div class="case-copy" data-case-reveal>
            <p class="chapter-intro">Econographer, a Microsoft Word add-in</p>
            <p>
              In my third year of high school, I wondered whether the economics
              diagrams we used in AP and IB coursework could be generated and
              adjusted in the document where we needed them. I could not find a
              suitable tool, so I made Econographer as a Microsoft Word add-in.
              Users choose a graph type, adjust its labels, curves, and
              equilibrium points, then insert the diagram into their document.
            </p>
            <p>
              The video follows a graph being created, adjusted, and inserted
              into Word.
            </p>
          </div>
          <div class="case-stage" data-case-reveal>
            <div class="case-stage-content">
              <video style="width: 80%" controls preload="metadata">
                <source src="/assets/vid/econographer.mp4" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
        </div>
      </section>
    </article>
  </div>
</template>
<script>
import ProjectPageHeader from "./ui/ProjectPageHeader.vue";
import projectCaseStudyMixin from "../mixins/projectCaseStudyMixin";
import ProjectChapterNav from "./ui/ProjectChapterNav.vue";

export default {
  computed: {
    chapters() {
      return [
        { id: "case-chapter-0", label: "Desktop apps" },
        { id: "case-chapter-1", label: "Podcasts" },
        { id: "case-chapter-2", label: "Extensions" },
        { id: "case-chapter-3", label: "Music" },
        { id: "case-chapter-4", label: "Website" },
        { id: "case-chapter-5", label: "Automation" },
        { id: "case-chapter-6", label: "Diagrams" },
      ];
    },
  },
  name: "EarlyPage",
  components: { ProjectChapterNav, ProjectPageHeader },
  mixins: [projectCaseStudyMixin],
};
</script>
